// src/components/payments/ApprovalModal.jsx
import React from 'react';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  X, 
  User, 
  DollarSign,
  Calendar,
  Building2
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const ApprovalModal = ({
  request,
  isOpen,
  action,
  rejectionReason,
  onReasonChange,
  approvalMessage,
  onApprovalMessageChange,
  onClose,
  onConfirm
}) => {
  if (!isOpen || !request) return null;

  const isApproval = action === 'approve';
  const isRejection = action === 'reject';

  const getUserTypeLabel = (userType) => {
    switch (userType) {
      case 'PHARMACIST': return 'Pharmacien';
      case 'SELLER': return 'Vendeur';
      case 'LABORATORY': return 'Laboratoire';
      case 'ADMIN': return 'Admin';
      default: return userType;
    }
  };

  const handleConfirm = () => {
    if (isRejection && !rejectionReason.trim()) {
      alert('Veuillez fournir une raison pour le rejet.');
      return;
    }
    onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className={`p-2 rounded-lg mr-3 ${
              isApproval 
                ? 'bg-green-100' 
                : 'bg-red-100'
            }`}>
              {isApproval ? (
                <CheckCircle className="h-6 w-6 text-green-600" />
              ) : (
                <XCircle className="h-6 w-6 text-red-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isApproval ? 'Approuver la demande' : 'Rejeter la demande'}
              </h3>
              <p className="text-sm text-gray-500">
                {isApproval 
                  ? 'Confirmer l\'approbation de cette demande de paiement' 
                  : 'Confirmer le rejet de cette demande de paiement'
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
          >
            <X size={20} />
          </button>
        </div>

        {/* Warning for approval */}
        {isApproval && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-amber-800">
                  Attention
                </h4>
                <p className="mt-1 text-sm text-amber-700">
                  Une fois approuvée, cette demande sera traitée et le montant sera versé à l'utilisateur. 
                  Cette action ne pourra pas être annulée.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Request Summary */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="text-md font-semibold text-gray-900 mb-4">
            Résumé de la demande
          </h4>
          
          <div className="space-y-4">
            {/* User Info */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-emerald-400 to-green-500 flex items-center justify-center mr-3">
                  <span className="text-white font-medium text-sm">
                    {(request.user?.firstName?.[0] || '') + (request.user?.lastName?.[0] || '')}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {request.user?.firstName} {request.user?.lastName}
                  </div>
                  <div className="text-sm text-gray-500 flex items-center">
                    <User className="h-3 w-3 mr-1" />
                    {getUserTypeLabel(request.user?.userType)}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-emerald-600">
                  {request.amount.toLocaleString('fr-FR', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })} DH
                </div>
                <div className="text-xs text-gray-500 flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  {format(new Date(request.dateDemande), 'dd/MM/yyyy', { locale: fr })}
                </div>
              </div>
            </div>

            {/* Organization */}
            {(request.user?.pharmacy || request.user?.ownedLaboratory) && (
              <div className="pt-3 border-t border-gray-200">
                <div className="flex items-center text-sm text-gray-600">
                  <Building2 className="h-4 w-4 mr-2" />
                  <span>
                    {request.user.pharmacy?.namePharmacy || request.user.ownedLaboratory?.nom}
                  </span>
                  {request.user.pharmacy?.region && (
                    <span className="ml-2 text-gray-500">
                      - {request.user.pharmacy.region.nomRegion}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Financial Summary */}
            <div className="pt-3 border-t border-gray-200">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-xs text-gray-500">Solde actuel</div>
                  <div className="text-sm font-medium text-gray-900">
                    {(request.user?.commissionBalance || 0).toFixed(2)} DH
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">En attente</div>
                  <div className="text-sm font-medium text-yellow-600">
                    {(request.user?.waitingCommission || 0).toFixed(2)} DH
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Approuvé</div>
                  <div className="text-sm font-medium text-green-600">
                    {(request.user?.approvedCommission || 0).toFixed(2)} DH
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Organization */}
            {request.organisme && (
              <div className="pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500 mb-1">Organisme de paiement</div>
                <div className="text-sm font-medium text-gray-900">
                  {request.organisme.nomOrganisme}
                </div>
                <div className="text-xs text-gray-500">
                  Contact: {request.organisme.prenomContact} {request.organisme.nomContact}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Rejection Reason Input */}
        {isRejection && (
          <div className="mt-6">
            <label htmlFor="rejectionReason" className="block text-sm font-medium text-gray-700 mb-2">
              Raison du rejet <span className="text-red-500">*</span>
            </label>
            <textarea
              id="rejectionReason"
              rows={4}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
              placeholder="Expliquez pourquoi cette demande est rejetée..."
              value={rejectionReason}
              onChange={(e) => onReasonChange(e.target.value)}
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Cette raison sera visible par l'utilisateur et dans l'historique.
            </p>
          </div>
        )}

        {/* Optional Approval Message */}
        {isApproval && (
          <div className="mt-6">
            <label htmlFor="approvalMessage" className="block text-sm font-medium text-gray-700 mb-2">
              Message (optionnel)
            </label>
            <textarea
              id="approvalMessage"
              rows={3}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              placeholder="Ajouter un message pour l'utilisateur (optionnel)…"
              value={approvalMessage || ''}
              onChange={(e) => onApprovalMessageChange?.(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500">
              Ce message sera inclus dans la notification envoyée à l'utilisateur.
            </p>
          </div>
        )}

        {/* Action Confirmation */}
        <div className="mt-6 p-4 border-l-4 border-gray-200 bg-gray-50">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              {isApproval ? (
                <CheckCircle className="h-5 w-5 text-green-400" />
              ) : (
                <XCircle className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-gray-900">
                {isApproval ? 'Conséquences de l\'approbation:' : 'Conséquences du rejet:'}
              </h4>
              <ul className="mt-2 text-sm text-gray-600 space-y-1">
                {isApproval ? (
                  <>
                    <li>• Le montant sera transféré vers l'organisme de paiement</li>
                    <li>• La commission en attente sera déplacée vers "commission approuvée"</li>
                    <li>• L'utilisateur recevra une notification de confirmation</li>
                    <li>• Le statut de la demande passera à "Approuvée"</li>
                  </>
                ) : (
                  <>
                    <li>• La demande sera marquée comme rejetée</li>
                    <li>• Le montant retournera dans le solde de l'utilisateur</li>
                    <li>• L'utilisateur recevra une notification avec la raison du rejet</li>
                    <li>• L'utilisateur pourra faire une nouvelle demande</li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            className={`px-6 py-2 text-sm font-medium text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isApproval
                ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
            }`}
          >
            {isApproval ? 'Approuver la demande' : 'Rejeter la demande'}
          </button>
        </div>

        {/* ID and Timestamp */}
        <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
          <span>ID: {request.id}</span>
          <span>
            Créée le: {format(new Date(request.createdAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ApprovalModal;